/**
 * 色定数定義
 *
 * MIDASアプリケーション全体で使用される色を一元管理します。
 * これらの色はデータ可視化コンポーネント全体で一貫性を保つために使用されます。
 *
 * @deprecated カテゴリカルパレットは統一パレットシステム（palette-utils.ts）を使用してください。
 * このファイルはUI要素の色定数のみを提供します。
 */

import { getPaletteColors } from './palette-utils';

/**
 * カテゴリカルデータ用の12色パレット
 *
 * @deprecated 統一パレットシステムの `createDefaultCategoricalScale()` または
 * `getPaletteColors('midas-default')` を使用してください。
 *
 * このエクスポートは後方互換性のために残されています。
 */
export const CATEGORICAL_PALETTE = getPaletteColors('midas-default');

/**
 * アプリケーション全体の色定義
 */
export const COLORS = {
  /**
   * プライマリカラー（主要な色）
   */
  primary: {
    blue: '#3B82F6',
    blueDark: '#2563EB',
    blueLight: '#60A5FA',
  },
  
  /**
   * 選択状態の色（単一系列用）
   */
  selection: {
    primary: '#EF4444',      // 選択されたデータポイント（単一系列）
    primaryDark: '#DC2626',  // 選択ホバー状態
    primaryLight: '#F87171', // 部分選択
    stroke: '#B91C1C',       // 選択時のストローク（単一系列）
  },
  
  /**
   * インタラクティブ要素の色
   */
  interaction: {
    hoverBackground: 'rgba(59, 130, 246, 0.1)',   // ホバー時の背景
    selectionRect: 'rgba(59, 130, 246, 0.2)',     // 選択矩形
    selectionRectStroke: 'rgba(59, 130, 246, 1)', // 選択矩形の枠線
    focusRing: '#3B82F6',                          // キーボードフォーカス
  },
  
  /**
   * セマンティックカラー（意味を持つ色）
   */
  semantic: {
    success: '#10B981',  // 成功・正の値
    warning: '#F59E0B',  // 警告・注意
    error: '#EF4444',    // エラー・負の値
    info: '#3B82F6',     // 情報・中立
  },
  
  /**
   * 背景・UI要素の色
   */
  background: {
    chart: '#FFFFFF',    // チャート背景
    grid: '#E5E7EB',     // グリッド線
    axis: '#9CA3AF',     // 軸線
    text: '#374151',     // テキスト
    mutedText: '#6B7280', // 控えめなテキスト
    border: '#E5E7EB',   // ボーダー
  },

  /**
   * 可視化要素の色
   */
  visualization: {
    densityLine: '#888888',       // 密度曲線のストローク色（非グループ化時）
    contourStroke: '#333333',     // 等高線のストローク色
    referenceLineDefault: '#999999', // 参照線のデフォルト色
  },
  
  /**
   * グラデーション用カラー
   */
  gradients: {
    /**
     * 単一色相のグラデーション（青）
     */
    sequential: [
      '#EFF6FF', // Lightest
      '#DBEAFE',
      '#BFDBFE',
      '#93C5FD',
      '#60A5FA',
      '#3B82F6',
      '#2563EB',
      '#1D4ED8',
      '#1E40AF', // Darkest
    ],
    
    /**
     * 発散型グラデーション（赤-グレー-青）
     */
    diverging: [
      '#DC2626', // Red (negative)
      '#F87171',
      '#FCA5A5',
      '#FECACA',
      '#FEE2E2',
      '#F3F4F6', // Neutral gray
      '#DBEAFE',
      '#BFDBFE',
      '#93C5FD',
      '#60A5FA',
      '#3B82F6', // Blue (positive)
    ],
  },
} as const;

/**
 * 選択時の視覚効果定数（複数系列用）
 * 
 * 複数系列の場合、色を変えずに視覚的強調を行う
 */
export const SELECTION_EFFECTS = {
  /**
   * 縁取り効果
   */
  stroke: '#1F2937',           // ダークグレー（黒に近い）
  strokeWidth: 2,               // 通常の2倍の太さ
  normalStrokeWidth: 0.5,       // 通常時の縁取り
  
  /**
   * サイズ変更
   */
  sizeMultiplier: 1.5,          // 選択時に1.5倍に拡大
  hoverSizeMultiplier: 1.2,     // ホバー時に1.2倍
  
  /**
   * 不透明度
   */
  normalOpacity: 0.8,           // 通常時の不透明度
  selectedOpacity: 1.0,         // 選択時の不透明度（完全不透明）
  unselectedOpacity: 0.3,       // 他が選択された時の非選択要素
  
  /**
   * アニメーション
   */
  transitionDuration: 200,      // ミリ秒
  transitionEasing: 'ease-out', // イージング関数
} as const;

/**
 * 系列インデックスから色を取得するヘルパー関数
 * @param index 系列のインデックス
 * @returns 対応する色コード
 */
export function getSeriesColor(index: number): string {
  return CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length];
}

/**
 * データ系列数に応じた色パレットを生成
 * @param count 系列数
 * @returns 色コードの配列
 */
export function generateSeriesColors(count: number): string[] {
  if (count === 1) {
    // 単一系列の場合はプライマリブルーを使用
    return [COLORS.primary.blue];
  }
  
  // 複数系列の場合はカテゴリカルパレットから必要数を取得
  const colors: string[] = [];
  for (let i = 0; i < count; i++) {
    colors.push(getSeriesColor(i));
  }
  return colors;
}

/**
 * 選択状態に応じた色を取得（単一系列用）
 * @param isSelected 選択されているかどうか
 * @param defaultColor デフォルトの色
 * @param isSingleSeries 単一系列かどうか
 * @returns 適切な色コード
 */
export function getSelectionColor(isSelected: boolean, defaultColor: string = COLORS.primary.blue, isSingleSeries: boolean = true): string {
  // 複数系列の場合は色を変えない
  if (!isSingleSeries) {
    return defaultColor;
  }
  // 単一系列の場合のみ赤に変更
  return isSelected ? COLORS.selection.primary : defaultColor;
}

/**
 * 選択状態に応じた視覚効果を取得（複数系列用）
 * @param isSelected 選択されているかどうか
 * @param hasSelection 何か選択されているかどうか
 * @returns 視覚効果のスタイルオブジェクト
 */
export function getSelectionEffects(isSelected: boolean, hasSelection: boolean = false) {
  return {
    opacity: isSelected 
      ? SELECTION_EFFECTS.selectedOpacity 
      : hasSelection 
        ? SELECTION_EFFECTS.unselectedOpacity 
        : SELECTION_EFFECTS.normalOpacity,
    stroke: isSelected ? SELECTION_EFFECTS.stroke : undefined,
    strokeWidth: isSelected 
      ? SELECTION_EFFECTS.strokeWidth 
      : SELECTION_EFFECTS.normalStrokeWidth,
  };
}

/**
 * ホバー状態に応じた色を取得
 * @param isHovered ホバーされているかどうか
 * @param baseColor ベースの色
 * @param isSelected 選択されているかどうか
 * @returns 適切な色コード
 */
export function getHoverColor(isHovered: boolean, baseColor: string, isSelected: boolean = false): string {
  if (isSelected) {
    return isHovered ? COLORS.selection.primaryDark : COLORS.selection.primary;
  }
  
  // ホバー時は少し暗い色を返す（簡易実装）
  // TODO: より洗練された色の明度調整を実装
  return isHovered ? COLORS.primary.blueDark : baseColor;
}

/**
 * グラフコンポーネント用のデフォルト色設定
 */
export const DEFAULT_CHART_COLORS = {
  // 単一系列のデフォルト
  single: {
    fill: COLORS.primary.blue,
    stroke: COLORS.primary.blueDark,
    selected: COLORS.selection.primary,
    selectedStroke: COLORS.selection.stroke,
  },
  
  // 複数系列のデフォルト
  multi: CATEGORICAL_PALETTE,
  
  // 軸・グリッドのデフォルト
  axis: {
    line: COLORS.background.axis,
    text: COLORS.background.mutedText,
    grid: COLORS.background.grid,
  },
  
  // ツールチップのデフォルト
  tooltip: {
    background: 'rgba(0, 0, 0, 0.8)',
    text: '#FFFFFF',
    border: 'rgba(255, 255, 255, 0.1)',
  },
} as const;

// 型定義
export type ColorPalette = typeof CATEGORICAL_PALETTE;
export type ColorScheme = typeof COLORS;
export type ChartColors = typeof DEFAULT_CHART_COLORS;